import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {FormControl} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {UsersService} from '@src/app/admin/users/services/users.service';
import {debounceTime, distinctUntilChanged, Subject, takeUntil} from 'rxjs';

interface ImpersonationUser {
    id: number;
    name: string;
    email: string;
    tenants?: Array<{ id: string; name: string }>;
}

interface TenantGroup {
    tenantName: string;
    users: ImpersonationUser[];
}

interface ImpersonateDialogData {
    currentUserId: number | null;
}

@Component({
    standalone: false,
    selector: 'app-impersonate-dialog',
    templateUrl: './impersonate-dialog.component.html',
})
export class ImpersonateDialogComponent implements OnInit, OnDestroy
{
    public searchControl: FormControl<string | null> = new FormControl('');
    public userControl: FormControl<number | null> = new FormControl<number | null>(null);

    public loading: boolean = false;
    public errorMessage: string = '';
    public hasSearched: boolean = false;

    public filteredUsers: ImpersonationUser[] = [];
    public tenantGroups: TenantGroup[] = [];

    private readonly minSearchLength = 2;

    private destroy$ = new Subject<void>();

    public constructor(
        private usersService: UsersService,
        private dialogRef: MatDialogRef<ImpersonateDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ImpersonateDialogData,
    )
    {
    }

    public ngOnInit(): void
    {
        // Only search when user has typed at least minSearchLength characters
        this.searchControl.valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            takeUntil(this.destroy$),
        ).subscribe(value => {
            const term = value ?? '';
            if (term.trim().length >= this.minSearchLength) {
                this.loadUsers(term.trim());
            } else if (!this.userControl.value) {
                // Only clear results if no user is selected; preserve groups when
                // ngx-mat-select-search resets the input after the panel closes.
                this.filteredUsers = [];
                this.tenantGroups = [];
                this.hasSearched = false;
            }
        });
    }

    public ngOnDestroy(): void
    {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public close(): void
    {
        this.dialogRef.close();
    }

    public confirm(): void
    {
        const userId = this.userControl.value;

        if (!userId) {
            return;
        }

        const selectedUser = this.filteredUsers.find(user => user.id === userId);

        this.dialogRef.close({
            userId,
            userName: selectedUser?.name ?? '',
        });
    }

    private loadUsers(searchTerm: string): void
    {
        this.loading = true;
        this.errorMessage = '';
        this.hasSearched = true;
        // Clear any previous selection when a new search is triggered
        this.userControl.setValue(null, { emitEvent: false });

        this.usersService.listCrossTenant(searchTerm).subscribe({
            next: response => {
                const raw: any[] = Array.isArray(response?.data)
                    ? response.data
                    : (Array.isArray(response) ? response : []);

                this.filteredUsers = raw
                    .filter((user: any) => user?.id && user?.name && user?.email)
                    .filter((user: any) => user.id !== this.data?.currentUserId)
                    .map((user: any) => ({
                        id: user.id,
                        name: user.name,
                        email: user.email,
                        tenants: Array.isArray(user.tenants) ? user.tenants : [],
                    }))
                    .sort((a: ImpersonationUser, b: ImpersonationUser) => a.name.localeCompare(b.name));

                this.tenantGroups = this.buildTenantGroups(this.filteredUsers);
                this.loading = false;
            },
            error: error => {
                this.errorMessage = error?.error?.message ?? 'Unable to load users for impersonation.';
                this.loading = false;
            }
        });
    }

    private buildTenantGroups(users: ImpersonationUser[]): TenantGroup[]
    {
        const groupMap = new Map<string, TenantGroup>();

        for (const user of users) {
            const userTenants = (user.tenants && user.tenants.length > 0)
                ? user.tenants
                : [{ id: '', name: 'No Tenant' }];

            for (const tenant of userTenants) {
                const key = tenant.id || 'no-tenant';
                if (!groupMap.has(key)) {
                    groupMap.set(key, { tenantName: tenant.name, users: [] });
                }
                // Avoid duplicates if a user appears in multiple tenants
                const group = groupMap.get(key)!;
                if (!group.users.find(u => u.id === user.id)) {
                    group.users.push(user);
                }
            }
        }

        return Array.from(groupMap.values())
            .sort((a, b) => a.tenantName.localeCompare(b.tenantName));
    }
}


